import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { Spinner } from "components/Loader";
import { VideoThumbnail } from "components/Video";
import { useInterviewQuestionAIVideoContext } from "hooks";
import { AI_VIDEO_TASK_DISPLAY_STATE, getVideoTaskDisplayState, resolveVideoTask } from "constants/index";
import { colors, fontSize } from "style";

/**
 * InterviewQuestionVideoThumbnailState
 *
 * @param {Object}  question                  The interview question object containing video and AI generation task details.
 * @param {string}  title                     The title for the video thumbnail, used for alt text and display.
 * @param {string}  className                 Optional additional class name for styling.
 * @param {boolean} canManageVideoGeneration  Flag indicating if the user has permissions to manage video generation tasks.
 *
 * This component displays the appropriate thumbnail or state for an interview question's video based on its AI generation task status.
 * It handles three main states:
 * 1. No video available: Shows a placeholder indicating the video is not available.
 * 2. Video generation pending: Shows a loading spinner and a message indicating that the video is being generated.
 * 3. Video available: Shows the video thumbnail with an overlay to indicate it can be clicked for review.
 */
const InterviewQuestionVideoThumbnailState = ({
  question,
  title,
  className,
  canManageVideoGeneration = false,
  ...props
}) => {
  const { openVideoActionModal, getTask } = useInterviewQuestionAIVideoContext();
  const cachedTask = getTask(question?.id);
  const task = resolveVideoTask({ cachedTask, questionTask: question?.aiVideoGenerationTask });
  const displayState = getVideoTaskDisplayState(task, {
    hasQuestionVideo: Boolean(question?.video),
    requireReviewThumbnail: true,
  });

  if (
    displayState === AI_VIDEO_TASK_DISPLAY_STATE.PROCESSING ||
    displayState === AI_VIDEO_TASK_DISPLAY_STATE.SUBMITTING
  ) {
    return <PendingInterviewTask className={className} />;
  }

  if (displayState === AI_VIDEO_TASK_DISPLAY_STATE.FINALIZING) {
    return (
      <PendingInterviewTask
        className={className}
        messageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Uploading.Thumbnail"
      />
    );
  }

  if (displayState === AI_VIDEO_TASK_DISPLAY_STATE.ERROR) {
    return (
      <CompletedInterviewTask
        thumbnail={task.thumbnailUrl}
        title={title}
        onClick={() => openVideoActionModal(question)}
        className={className}
        chipMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Error.Tile"
        chipVariant="error"
        {...props}
      />
    );
  }

  if (displayState === AI_VIDEO_TASK_DISPLAY_STATE.REVIEW && task?.thumbnailUrl) {
    return (
      <CompletedInterviewTask
        thumbnail={task.thumbnailUrl}
        title={title}
        onClick={() => openVideoActionModal(question)}
        className={className}
        {...props}
      />
    );
  }

  if (displayState === AI_VIDEO_TASK_DISPLAY_STATE.READY && question?.video) {
    return (
      <VideoThumbnail
        src={question.video}
        thumbnail={question.thumbnail}
        previewProps={{ title }}
        className={className}
        onSecondaryButtonClick={() => openVideoActionModal(question)}
        showSecondaryButton={canManageVideoGeneration}
        {...props}
      />
    );
  }

  return (
    <EmptyInterviewTask
      className={className}
      onClick={() => openVideoActionModal(question)}
      canManageVideoGeneration={canManageVideoGeneration}
    />
  );
};

/**
 * EmptyInterviewTask
 *
 * @param {function}  onClick                  The function to call when the "Add Video" button is clicked.
 * @param {boolean}   canManageVideoGeneration Flag indicating if the user has permissions to manage video generation tasks.
 *
 * This component displays a placeholder state for an interview question that does not have a video yet.
 * If the user has permissions to manage video generation, it shows an "Add Video" button; otherwise, it shows a message indicating that the video is not available.
 */
const EmptyInterviewTask = ({ onClick, canManageVideoGeneration, ...props }) => (
  <StateContainer {...props}>
    {canManageVideoGeneration ? (
      <ActionPillButton type="button" onClick={onClick}>
        <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.AddVideo" />
      </ActionPillButton>
    ) : (
      <GeneratingLabel>
        <FormattedMessage id="Video.VideoThumbnail.NotAvailable" />
      </GeneratingLabel>
    )}
  </StateContainer>
);

EmptyInterviewTask.propTypes = {
  onClick: PropTypes.func.isRequired,
  canManageVideoGeneration: PropTypes.bool.isRequired,
};

/**
 * PendingInterviewTask
 *
 * @param {string} messageId  The message ID for the loading state text.
 *
 * This component displays a loading spinner and a message indicating that the video is being generated or processed.
 */
const PendingInterviewTask = ({ messageId = "Missions.Roles.RoleVetting.Interviews.AIVideo.Generating", ...props }) => (
  <StateContainer {...props}>
    <GeneratingLabel>
      <FormattedMessage id={messageId} />
    </GeneratingLabel>
    <Spinner size="2rem" />
  </StateContainer>
);

PendingInterviewTask.propTypes = {
  messageId: PropTypes.string,
};

/**
 * CompletedInterviewTask
 *
 * @param {string}    thumbnail      The URL of the video thumbnail image.
 * @param {string}    title          The title for the video thumbnail, used for alt text and display.
 * @param {function}  onClick        The function to call when the thumbnail is clicked.
 * @param {string}    className      Optional additional class name for styling.
 * @param {string}    chipMessageId  The message ID for the overlay chip text.
 * @param {string}    chipVariant    The variant of the overlay chip (e.g., "default", "error").
 *
 * This component displays the video thumbnail for an interview question that has a completed video generation task.
 * It includes an overlay chip to indicate the status of the video (e.g., "Review", "Error").
 */
const CompletedInterviewTask = ({
  thumbnail,
  title,
  onClick,
  className,
  chipMessageId = "Missions.Roles.RoleVetting.Interviews.AIVideo.Review",
  chipVariant = "default",
}) => (
  <PreviewWrapper onClick={onClick} className={className}>
    <PreviewImage src={thumbnail} alt={title} />
    <PreviewOverlay>
      <PreviewChip variant={chipVariant}>
        <FormattedMessage id={chipMessageId} />
      </PreviewChip>
    </PreviewOverlay>
  </PreviewWrapper>
);

CompletedInterviewTask.propTypes = {
  thumbnail: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  chipMessageId: PropTypes.string,
  chipVariant: PropTypes.oneOf(["default", "error"]),
};

const baseBoxStyles = `
  aspect-ratio: 16 / 9;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const StateContainer = styled.div`
  ${baseBoxStyles}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: ${colors.grayAnatomyLight5};
`;

const ActionPillButton = styled.button`
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: ${colors.grayAnatomyBase};
  font-size: ${fontSize.xsmall};
  font-weight: 600;
  padding: 0.6rem 1rem;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const GeneratingLabel = styled.div`
  font-size: ${fontSize.xsmall};
  color: ${colors.grayAnatomyLight1};
  text-align: center;
`;

const PreviewWrapper = styled.button`
  ${baseBoxStyles}
  position: relative;
  border: none;
  padding: 0;
  background: ${colors.black};
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);

  &:hover {
    background: rgba(0, 0, 0, 0.42);
  }
`;

const PreviewChip = styled.div`
  padding: 0.6rem 1rem;
  border-radius: 999px;
  background: ${({ variant }) => (variant === "error" ? "rgba(255, 235, 238, 0.96)" : "rgba(255, 255, 255, 0.92)")};
  color: ${({ variant }) => (variant === "error" ? colors.red : colors.grayAnatomyBase)};
  font-size: ${fontSize.xsmall};
  font-weight: 600;
`;

InterviewQuestionVideoThumbnailState.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    video: PropTypes.string,
    thumbnail: PropTypes.string,
    aiVideoGenerationTask: PropTypes.object,
  }).isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  canManageVideoGeneration: PropTypes.bool,
};

export default InterviewQuestionVideoThumbnailState;
